import { useEffect, useRef } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

const CALENDLY_URL = 'https://paystack.com/buy/private-clarity-call-sdonrr';

const offerings = [
  {
    icon: 'ri-user-voice-line',
    title: '1:1 Coaching & Therapy',
    description:
      'This is the most intimate work Mandie offers. No templates, no programmes, just you, your story, and a space built entirely around your becoming. Women who do this work describe it as the first time they have ever felt truly met. If you are ready to go to the root, this is where you start.',
    featured: true,
    buttonText: 'Book a Clarity Call',
    buttonStyle: 'magenta' as const,
    href: CALENDLY_URL,
  },
  {
    icon: 'ri-group-line',
    title: 'Group Coaching Sessions',
    description:
      'There is something that happens when a woman realises she is not alone in what she has been carrying. Eight weeks, a small circle of women, and a structure designed to take you somewhere you could not have reached by yourself. You will leave with more than insight. You will leave with a new relationship with yourself, and women who witnessed the shift.',
    featured: false,
    buttonText: 'Book a Clarity Call',
    buttonStyle: 'magenta' as const,
    href: CALENDLY_URL,
  },
  {
    icon: 'ri-road-map-line',
    title: 'H.O.M.E',
    description:
      'Healed. Owned. Met. Embodied. Twelve weeks of structured, self-paced work built by a clinical psychologist for the woman who is ready to go deep, on her own time, at her own pace. Women who complete the H.O.M.E often say the same thing: they did not realise how far from themselves they had drifted until they found their way back. This is the map home.',
    featured: false,
    buttonText: 'Book a Clarity Call',
    buttonStyle: 'magenta' as const,
    href: CALENDLY_URL,
  },
  {
    icon: 'ri-sun-line',
    title: 'The Retreat',
    description:
      'Some healing cannot happen in the middle of your ordinary life. Sometimes you need to step entirely out of it. The retreat is an annual immersive experience, beautiful surroundings, deep somatic and psychological work, and the kind of transformation that only happens when you give yourself full permission to show up for yourself. Women leave different. That is not a metaphor.',
    featured: false,
    buttonText: 'Book a Clarity Call',
    buttonStyle: 'magenta' as const,
    href: CALENDLY_URL,
  },
  {
    icon: 'ri-megaphone-line',
    title: 'Women Who Heal Conference',
    description:
      'This year, for the first time, Mandie is gathering the most extraordinary women in one room, and something is already shifting in the air.',
    featured: false,
    buttonText: 'Join the Waitlist',
    buttonStyle: 'gold' as const,
    href: '#women-who-heal',
  },
];

export default function CoachingSuiteSection() {
  const sectionRef = useRef<HTMLElement>(null);
  const headerRef = useRef<HTMLDivElement>(null);
  const cardsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.fromTo(
        headerRef.current,
        { opacity: 0, y: 30 },
        {
          opacity: 1,
          y: 0,
          duration: 1,
          scrollTrigger: {
            trigger: sectionRef.current,
            start: 'top 70%',
          },
        }
      );
      if (cardsRef.current) {
        const cardEls = cardsRef.current.children;
        gsap.fromTo(
          cardEls,
          { opacity: 0, y: 30 },
          {
            opacity: 1,
            y: 0,
            duration: 0.8,
            stagger: 0.12,
            scrollTrigger: {
              trigger: cardsRef.current,
              start: 'top 80%',
            },
          }
        );
      }
    }, sectionRef);

    return () => ctx.revert();
  }, []);

  return (
    <section
      id="coaching-suite"
      ref={sectionRef}
      className="bg-[#FDFAF8] py-16 md:py-28 lg:py-32"
    >
      <div className="w-full px-4 md:px-8 lg:px-12 max-w-6xl mx-auto">
        {/* Header */}
        <div ref={headerRef} className="text-center mb-14">
          <p className="text-[#C9A84C] text-xs font-body tracking-[0.25em] uppercase mb-6">
            The coaching suite
          </p>
          <h2 className="font-heading text-2xl md:text-4xl lg:text-5xl leading-[1.2] text-[#1A1A1A] mb-5">
            There is a container for every woman.
            <br />
            The only question is: which one is calling you?
          </h2>
          <p className="font-body text-sm md:text-base text-[#4A4A4A] leading-relaxed max-w-2xl mx-auto">
            Every offer inside the Mandie Ozee coaching suite is designed to meet
            you exactly where you are, and take you somewhere you could not have
            reached alone.
          </p>
        </div>

        {/* Gold Divider */}
        <div className="w-16 h-px bg-[#C9A84C] mx-auto mb-14" />

        {/* Cards Grid */}
        <div
          ref={cardsRef}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6 justify-items-center"
        >
          {offerings.map((item, index) => (
            <div
              key={index}
              className={`relative bg-[#FFFFFF] rounded-2xl p-5 md:p-8 flex flex-col w-full max-w-[380px] overflow-hidden group transition-shadow duration-300 hover:shadow-[0_2px_20px_rgba(0,0,0,0.06)] ${
                item.featured
                  ? 'border border-[#C9A84C]/50 ring-1 ring-[#C9A84C]/20'
                  : 'border border-[#E8D5C8]'
              }`}
            >
              {/* Gold accent bar at top */}
              <div className={`absolute top-0 left-6 right-6 h-0.5 rounded-full ${item.featured ? 'bg-[#C9A84C]' : 'bg-[#C9A84C]/25'}`} />

              {/* Featured badge */}
              {item.featured && (
                <span className="absolute top-3 right-6 px-3 py-1 bg-[#C9A84C] text-[#1A1A1A] text-[10px] font-body font-medium tracking-wider uppercase rounded-full">
                  Featured
                </span>
              )}

              {/* Icon */}
              <div className="w-10 h-10 flex items-center justify-center mb-5">
                <i className={`${item.icon} text-2xl ${item.featured ? 'text-[#C9A84C]' : 'text-[#C9A84C]/60'}`}></i>
              </div>

              {/* Title */}
              <h3 className="font-heading text-lg md:text-xl text-[#1A1A1A] mb-3">
                {item.title}
              </h3>

              {/* Description */}
              <p className="font-body text-sm text-[#4A4A4A] leading-relaxed mb-6 flex-grow">
                {item.description}
              </p>

              {/* CTA Button */}
              <a
                href={item.href}
                target={item.href.startsWith('http') ? '_blank' : undefined}
                rel={item.href.startsWith('http') ? 'noopener noreferrer' : undefined}
                className={`inline-flex items-center justify-center px-6 py-3 text-sm font-body font-medium rounded-full transition-colors duration-300 whitespace-nowrap ${
                  item.buttonStyle === 'magenta'
                    ? 'bg-[#D4006A] text-white hover:bg-[#B3005A]'
                    : 'border border-[#C9A84C] text-[#C9A84C] hover:bg-[#C9A84C] hover:text-[#1A1A1A]'
                }`}
              >
                {item.buttonText}
              </a>
              <p className="text-xs text-[#4A4A4A] mt-2">
                <a href="https://paystack.com/buy/private-clarity-call-sdonrr" target="_blank" rel="noopener noreferrer" className="underline hover:text-[#D4006A]">Book a Private Clarity Call</a>
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}